import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { getConsentClosedFromLocalStorage } from '../routes/Consent/utils/consentUtils'
import { SESSION_STORAGE_KEYS } from 'utils/constants'
import { getCurrentPath, replaceTo } from 'navigation'

const getDisplayName = (Component) =>
  Component.displayName || Component.name || 'Component'

const hasValidSession = (user) =>
  Boolean(user?.token && user?.exp && user.exp * 1000 > Date.now())

const withAuthGuard =
  ({ authSelector, predicate, failureRedirectPath, wrapperDisplayName }) =>
    (Component) => {
      const Guard = (props) => {
        const auth = useSelector(authSelector)
        const isAllowed = predicate(auth, props)
        const currentPath = props?.location?.pathname || ''
        const currentRedirect = props?.location?.query?.redirect || ''
        const lastRedirectPathRef = useRef('')

        useEffect(() => {
          if (isAllowed) {
            lastRedirectPathRef.current = ''
            return
          }
          const redirectPath =
            typeof failureRedirectPath === 'function'
              ? failureRedirectPath(auth, props)
              : failureRedirectPath

          if (!redirectPath) return

          const historyPath = getCurrentPath()
          const activePath = currentPath || historyPath

          if (redirectPath === activePath) return
          if (lastRedirectPathRef.current === redirectPath) return

          lastRedirectPathRef.current = redirectPath
          replaceTo(redirectPath)
        }, [auth, isAllowed, currentPath, currentRedirect])

        if (!isAllowed) return null
        return <Component {...props} authData={auth} />
      }

      Guard.displayName = `${wrapperDisplayName}(${getDisplayName(Component)})`
      return Guard
    }

const selectUserAndConsent = (() => {
  let prevUser
  let prevConsent
  let prevResult
  return (state) => {
    if (state.user === prevUser && state.consent === prevConsent && prevResult) {
      return prevResult
    }
    prevUser = state.user
    prevConsent = state.consent
    prevResult = { user: state.user, consent: state.consent }
    return prevResult
  }
})()

const selectUserAndCaregiver = (() => {
  let prevUser
  let prevCaregiver
  let prevResult
  return (state) => {
    if (
      state.user === prevUser &&
      state.caregiver === prevCaregiver &&
      prevResult
    ) {
      return prevResult
    }
    prevUser = state.user
    prevCaregiver = state.caregiver
    prevResult = { user: state.user, caregiver: state.caregiver }
    return prevResult
  }
})()

export const UserHasConsented = (Component) =>
  withAuthGuard({
    authSelector: selectUserAndConsent,
    predicate: ({ user, consent }) => {
      if (!user.study) return false
      // Caregivers should not be blocked by consent dialogues.
      if (user?.is_caregiver) return true
      const isBlockingConsent = consent?.consentForm?.is_blocking
      const isConsentClosed = getConsentClosedFromLocalStorage()

      if (!isBlockingConsent && isConsentClosed) {
        return true
      }

      return user.study.consent_required ? user.has_consented : true
    },
    failureRedirectPath: ({ user }) =>
      Object.keys(user || {}).length ? '/consent' : '/login',
    wrapperDisplayName: 'UserHasConsented'
  })(Component)

export const UserHasNotConsented = (Component) =>
  withAuthGuard({
    authSelector: (state) => state.user || {},
    predicate: (user) => {
      if (!user.study) return false
      return user.study.consent_required ? !user.has_consented : false
    },
    failureRedirectPath: (user) =>
      Object.keys(user || {}).length ? '/my-activities' : '/login',
    wrapperDisplayName: 'UserHasNotConsented'
  })(Component)

export const UserIsAuthenticated = (Component) =>
  withAuthGuard({
    authSelector: (state) => state.user || {},
    predicate: (user) => hasValidSession(user),
    failureRedirectPath: '/login',
    wrapperDisplayName: 'UserIsAuthenticated'
  })(Component)

export const UserHasScope = (requiredScopes = [], Component) =>
  withAuthGuard({
    authSelector: (state) => state.user || {},
    predicate: (user) =>
      Array.isArray(requiredScopes) &&
      requiredScopes.some(scope => user?.scope?.includes(scope)),
    failureRedirectPath: (user) =>
      hasValidSession(user) ? '/unauthorized' : '/login',
    wrapperDisplayName: 'UserHasScope'
  })(Component)

export const UserDoesNotHaveTempPassword = (Component) =>
  withAuthGuard({
    authSelector: (state) => state.user || {},
    predicate: (user) => !user.is_temporary_password,
    failureRedirectPath: (user) =>
      Object.keys(user || {}).length ? '/profile/change-password' : '/login',
    wrapperDisplayName: 'UserDoesNotHaveTempPassword'
  })(Component)

export const UserIsAuthenticatedAndIgnoreConsent = (Component) =>
  UserDoesNotHaveTempPassword(UserIsAuthenticated(Component))

export const UserIsAuthenticatedAndHasConsented = (Component) =>
  UserDoesNotHaveTempPassword(UserHasConsented(UserIsAuthenticated(Component)))

// Caregiver-specific guard: if caregiver has multiple participants and none selected,
// block access to app pages and send them to Select Participant.
export const CaregiverHasSelectedParticipant = (Component) =>
  withAuthGuard({
    authSelector: selectUserAndCaregiver,
    predicate: ({ user, caregiver }) => {
      // Non-caregivers are always allowed
      if (!user?.is_caregiver) return true
      const selected = caregiver?.selected
      // If caregiver has a selected participant, allow
      if (selected) return true
      // If there's exactly one participant assigned (from Redux or sessionStorage),
      // allow access so the auto-select flow can proceed without redirect flicker.
      const participants = caregiver?.participants || []
      if (Array.isArray(participants) && participants.length === 1) return true
      try {
        const stored = sessionStorage.getItem(
          SESSION_STORAGE_KEYS.caregiverParticipants
        )
        if (stored) {
          const parsed = JSON.parse(stored) || []
          if (Array.isArray(parsed) && parsed.length === 1) return true
        }
      } catch (_) { }
      // Otherwise, require them to go to the selector
      return false
    },
    failureRedirectPath: () => '/select-ptp',
    wrapperDisplayName: 'CaregiverHasSelectedParticipant'
  })(Component)

export const UserIsNotAuthenticated = (Component) =>
  withAuthGuard({
    authSelector: (state) => state.user || {},
    wrapperDisplayName: 'UserIsNotAuthenticated',
    failureRedirectPath: (_user, ownProps) =>
      ownProps?.location?.query?.redirect || '/',
    predicate: (user) => !hasValidSession(user)
  })(Component)